import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { routeTree } from "./routeTree.gen";
import "./styles.css";

/**
 * STALE-CHUNK AUTO-RECOVERY.
 *
 * After a deploy, the user's cached index.html may still reference an
 * older bundle hash (e.g. _app.ihub-OLDHASH.js). When TanStack Router
 * dynamically imports that chunk, Vite's preloader fires
 * `vite:preloadError`. Without this handler the user sees iOS Safari's
 * "'text/html' is not a valid JavaScript MIME type" error (the
 * Cloudflare Pages SPA fallback was serving index.html for the dead
 * asset). _redirects now returns 404 for missing /assets/* so the
 * error fires cleanly here.
 *
 * On the error: reload the page with a `?_v=<timestamp>` cache-buster
 * so the browser cannot serve the stale index.html from HTTP cache
 * either. One reload and the user is on the latest bundle. Add a
 * sentinel so we never reload twice in a row (prevents an infinite
 * loop if the actual chunk genuinely 404s).
 */
const RELOAD_SENTINEL = "ojaswati_chunk_reload_at";
window.addEventListener("vite:preloadError", (e) => {
  const last = Number(sessionStorage.getItem(RELOAD_SENTINEL) || "0");
  const now = Date.now();
  if (now - last < 30_000) {
    // Already tried within last 30s — let the error bubble; either the
    // chunk is genuinely missing or something more serious is broken.
    console.error("[chunk-reload] aborting: reloaded too recently", e);
    return;
  }
  sessionStorage.setItem(RELOAD_SENTINEL, String(now));
  console.warn("[chunk-reload] stale chunk detected, reloading with cache-buster", e);
  const url = new URL(window.location.href);
  url.searchParams.set("_v", String(now));
  window.location.replace(url.toString());
});

// Also handle generic chunk-load errors (e.g. React.lazy) — these
// surface as `error` events with the chunk URL in the message.
window.addEventListener("error", (e) => {
  const msg = String(e?.message || "");
  if (
    /Loading chunk \d+ failed|Failed to fetch dynamically imported module|'text\/html' is not a valid JavaScript MIME type/i.test(
      msg,
    )
  ) {
    const last = Number(sessionStorage.getItem(RELOAD_SENTINEL) || "0");
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem(RELOAD_SENTINEL, String(Date.now()));
    console.warn("[chunk-reload] generic chunk-load error, reloading", msg);
    const url = new URL(window.location.href);
    url.searchParams.set("_v", String(Date.now()));
    window.location.replace(url.toString());
  }
});

/**
 * ojaswati v2 client entry.
 *
 * Plain SPA. No SSR. The browser loads /index.html, this script mounts the
 * React app, TanStack Router takes over /ihub /library /askme /settings
 * routing via the file-based routeTree generated from src/routes/*.
 *
 * Backend API lives at /api/* and is served by Cloudflare Pages Functions
 * out of the repo's functions/ directory — completely separate from this
 * bundle. The client talks to it via fetch() through TanStack Query.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // SSE keeps the cache fresh; React Query doesn't need to poll.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  scrollRestoration: true,
  defaultPreloadStaleTime: 0,
});

// Strong typing for the router across the app.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Missing #root element in index.html");
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

/**
 * #273 — Service worker registration (scope-limited to Web Share
 * Target for WhatsApp / Voice Memos / Telegram audio sharing).
 *
 * Production only — in dev (vite serve) we skip registration so the
 * Vite HMR client isn't shadowed by a stale SW. Registers on window
 * 'load' so it doesn't compete with the React boot for CPU.
 *
 * If the SW file is missing or fails to register, the rest of the app
 * works fine — share-target is a progressive enhancement, not a
 * requirement.
 */
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => {
        console.warn("[sw] registration failed:", err);
      });
  });
}
